import React, { useCallback, useEffect, useState } from 'react';
import { Feature, Layer } from '@leverege/ui-google-maps-elements';
import { GlobalState, Selection, UI } from '@leverege/ui-redux';
import Path from '@leverege/path';
import I18N from '@leverege/i18n';
import { Dialogs } from '@leverege/ui-elements';
import CenterOnAddressInput from "../../views/CenterOnAddressInput.js";
import OkayCancelView from "../map/OkayCancelView.js";
import S from './GeoJsonPointer.css';
import LoadingView from "../../views/LoadingView.js";
import CreateItem from "../view/CreateItem.js";
import { jsx as _jsx } from "react/jsx-runtime";
const {
  t
} = I18N.ns('molten.imagine.map.GeoJsonPointer');
export default class GeoJsonPointer {
  constructor(options) {
    const {
      relationship,
      feature,
      attribute,
      target,
      map
    } = options;
    this.feature = feature;
    this.isDealloced = false;
    this.props = options;
    this.attribute = attribute;
    this.help = /*#__PURE__*/React.createRef();
    this.buttons = /*#__PURE__*/React.createRef();
    this.target = target;
    this.isEditing = target != null;
    this.mode = feature ? 'direct_select' : 'draw_polygon';
    this.map = map;
    this.controller = /*#__PURE__*/React.createElement(SingleShapeControl, {
      key: `${relationship.object}-${Date.now()}`,
      feature,
      onUnmount: this.onCancel,
      onInsertAt: this.onInsertAt,
      onRemoveAt: this.onRemoveAt,
      onSetAt: this.onSetAt,
      onMount: this.updateComponents
    });
  }
  onPointerInstalled() {
    if (this.isEditing) {
      GlobalState.dispatch(Selection.set('mapEditing', [this.target]));
    }
    GlobalState.dispatch(UI.set('mapEditing', true));
    this.map.setHeading(0);
    this.map.setTilt(0);
  }
  onPointerRemoved() {
    if (this.target) {
      GlobalState.dispatch(Selection.remove('mapEditing', this.target));
    }
    GlobalState.dispatch(Selection.clearAll());
    GlobalState.dispatch(UI.set('mapEditing', false));
  }
  onInsertAt = (evt, ...other) => {
    if (this.feature == null) {
      this.updated = true;
    }
    this.mode = 'direct_select';
    this.feature = evt.feature;
    this.updateComponents();
  };
  onRemoveAt = evt => {
    this.feature = null;
    this.updateComponents();
  };
  onSetAt = evt => {
    this.feature = evt.feature;
    this.updated = true;
    this.updateComponents();
  };
  isTriangle() {
    if (this.feature == null) {
      return false;
    }
    return typeof this.feature.getGeometry === 'function' ? this.feature.getGeometry().getArray()[0].getArray().length <= 2 : this.feature?.geometry?.coordinates?.[0]?.length <= 2;
    // if ( this.feature == null ) {
    //   return false
    // }
    // // we use 4 instead of 3 because this is for polygons only, and they are closed 
    // // by including the final point.
    // return this.feature?.geometry?.coordinates?.[0]?.length <= 4
  }
  updateComponents = () => {
    let h = t('create');
    let title = t('drawTitle');
    let ready = true;
    if (this.mode === 'simple_select') {
      h = t('move');
      title = t('editTitle');
    } else if (this.mode === 'direct_select') {
      h = this.isTriangle() ? t('edit2') : t('edit');
      title = t('editTitle');
    } else {
      ready = false;
    }
    if (this.buttons.current) {
      this.buttons.current.setHelp(`${title} ${this.attribute.name}`, h);
      this.buttons.current.setOkayDisabled(this.isEditing && !this.updated || !ready || this.isTriangle());
    }
  };
  onCreate = evt => {
    const {
      mapViewer
    } = this.pointerContext;
    const {
      delegate,
      attribute,
      relationship
    } = this.props;
    const initialData = {
      data: {}
    };
    this.feature.toGeoJson(data => {
      Path.set(attribute.path, initialData.data, JSON.stringify({
        ...data,
        properties: {}
      }));
      Dialogs.show({
        component: CreateItem,
        props: {
          delegate,
          relationship,
          initialData
        }
      });
      if (mapViewer) {
        mapViewer.resetPointer();
      }
    });
  };
  onSave = evt => {
    const {
      mapViewer
    } = this.pointerContext;
    const {
      delegate,
      attribute
    } = this.props;
    const initialData = {
      data: {}
    };
    const close = LoadingView.showDialog(t('savingDialog', {
      attributeName: attribute.blueprint.name
    }));
    this.feature.toGeoJson(data => {
      Path.set(attribute.path, initialData.data, JSON.stringify({
        ...data,
        properties: {}
      }));
      GlobalState.dispatch(delegate.update(initialData))
      // eslint-disable-next-line no-console
      .then(() => {}, console.log).then(() => {
        if (mapViewer) {
          mapViewer.resetPointer();
        }
        close();
      });
    });
  };
  onCancel = () => {
    const {
      mapViewer
    } = this.pointerContext;
    if (mapViewer) {
      mapViewer.resetPointer();
    }
  };
  setPointerContext(context) {
    this.pointerContext = context;
    this.mapViewer = context.mapViewer;
  }
  render() {
    return this.controller;
  }
  getComponents() {
    const h = this.isEditing ? this.isTriangle() ? t('edit2') : t('edit') : t('create');
    return [/*#__PURE__*/_jsx("div", {
      className: S.address,
      children: /*#__PURE__*/_jsx(CenterOnAddressInput, {})
    }, "address"), /*#__PURE__*/_jsx(OkayCancelView, {
      okay: this.isEditing ? t('save') : t('next'),
      ref: this.buttons,
      help: h,
      title: `${this.isEditing ? t('editTitle') : t('drawTitle')} ${this.attribute.name}`,
      onCreate: this.isEditing ? this.onSave : this.onCreate,
      onCancel: this.onCancel
    }, "createCancel")];
  }
}
function SingleShapeControl(props) {
  const {
    feature,
    onUnmount,
    onMount,
    onInsertAt: pOnInsertAt,
    onRemoveAt,
    onSetAt
  } = props;
  const [featureData, setFeatureData] = useState();
  const [drawingMode, setDrawingMode] = useState(feature != null ? null : 'Polygon');
  const onInsertAt = useCallback((evt, ...other) => {
    setDrawingMode(null);
    pOnInsertAt(evt);
  }, [pOnInsertAt]);
  const getLineStyle = useCallback(feature => {
    const lineSymbol = {
      path: 'M 0,-1 0,0',
      scale: 4,
      strokeWeight: 1,
      strokeOpacity: 1,
      strokeColor: 'red'
    };
    const icons = [{
      icon: lineSymbol,
      offset: '0',
      repeat: '10px'
    }];
    return {
      editable: true,
      draggable: true,
      fillOpacity: 0.1,
      fillColor: 'red',
      icons,
      strokeOpacity: 0.4
    };
  }, []);
  useEffect(() => {
    if (feature) {
      setFeatureData({
        ...feature,
        properties: {}
      });
    }
    if (typeof onMount === 'function') {
      onMount();
    }
    return () => {
      if (typeof onUnmount === 'function') {
        onUnmount();
      }
    };
  }, []);
  return /*#__PURE__*/_jsx(Layer, {
    drawingMode: drawingMode,
    onInsertAt: onInsertAt,
    onRemoveAt: onRemoveAt,
    onSetAt: onSetAt,
    type: "fill",
    id: "editing-layer",
    style: getLineStyle,
    children: featureData && /*#__PURE__*/_jsx(Feature, {
      editable: true,
      id: featureData.id,
      coordinates: featureData.geometry.coordinates,
      properties: featureData.properties
    })
  });
}